/**
 * Today: only what needs doing and how we are keeping our promises, so nothing waits.
 *   - five tiles: what needs you now, replies on time, reports on time, clients this month, open pipeline
 *   - the work itself (replies, outreach, calls and follow-ups, audits), late first
 *   - beside it: the next 7 days as an agenda, and leads going quiet
 * The two promises: a reply within 2 working days, and the audit report within 5 working days of
 * complete data.
 */
import type { AuditRow, Db } from "../data/db.ts";
import { IMPLEMENTATION_WEEKS } from "../lib/implementation.ts";
import { isOpen, leadNextAction, triage, type LeadRow } from "../lib/leads.ts";
import { londonDate, workingDaysBetween } from "../lib/working-days.ts";
import { statusPill } from "./audits.ts";
import { date, gbp, h, mount, notice, stat, table } from "./dom.ts";
import { leadTitle, nextCell, priorityPill, stagePill, when } from "./leads.ts";
import { displayName } from "./shell.ts";
import { icon } from "./icons.ts";
import { prefs, setPrefs } from "./prefs.ts";
import { langAttrs, langNotice, t } from "./i18n.ts";
import { clientsThisMonth, implementationsOverrun, LOOKBACK_DAYS, pctText, replyPromise, reportPromise, STALE_DAYS, staleLeads } from "../lib/insights.ts";
import { pipelineValue } from "../lib/crm.ts";
import type { ReportRow } from "../data/db.ts";

const daysBetween = (a: string, b: string) => Math.round((Date.parse(`${b}T12:00:00Z`) - Date.parse(`${a}T12:00:00Z`)) / 864e5);

const WHATS_NEW = "2026-10-insights";

/** A one-off card about the new look, until it is dismissed. */
function whatsNew(): HTMLElement | "" {
  if (prefs().seenWhatsNew === WHATS_NEW) return "";
  const card: HTMLElement = h("div", { class: "card whats-new", role: "region", "aria-label": "What's new" },
    h("div", null,
      h("h2", null, icon("katana"), "What's new"),
      h("ul", { class: "checks" },
        h("li", null, "Today now shows only what matters: what needs you now, whether replies and reports are on time, clients this month, and the open pipeline."),
        h("li", null, "Beside your work: the next 7 days as an agenda, and leads going quiet (no contact for 14 days)."),
        h("li", null, h("a", { href: "#/insights" }, "Insights"), " shows the promises we keep month by month, where clients come from and audit turnaround."),
        h("li", null, "Press ", h("kbd", null, "Ctrl"), " ", h("kbd", null, "K"), " to find anything, and ", h("kbd", null, "?"), " for shortcuts. Theme, language and reading options are in ", h("a", { href: "#/settings" }, "Settings"), "."),
      ),
    ),
    h("button", { type: "button", class: "btn btn-quiet", onClick: () => { setPrefs({ seenWhatsNew: WHATS_NEW }); card.remove(); } }, "Got it"),
  );
  return card;
}

export async function todayView(db: Db, email: string): Promise<HTMLElement> {
  const [leads, audits, actions] = await Promise.all([db.listLeads(), db.listAudits(), db.listOpenActions()]);
  const now = new Date();
  const today = londonDate(now);
  const in7 = londonDate(new Date(now.getTime() + 7 * 864e5));
  const byAudit = new Map(audits.map((a) => [a.id, a]));

  // ---- Leads
  const open = leads.filter((l) => isOpen(l.status));
  const replies = open.filter((l) => l.status === "new").sort((a, b) => (a.reply_due ?? "").localeCompare(b.reply_due ?? ""));
  const lateReplies = replies.filter((l) => leadNextAction(l, now)?.overdue);
  const calls = open.filter((l) => l.status === "call_booked" && l.call_at && londonDate(new Date(l.call_at)) >= today && londonDate(new Date(l.call_at)) <= in7)
    .sort((a, b) => a.call_at!.localeCompare(b.call_at!));
  // Outbound touches due today or late, and sequences waiting to be closed.
  const touchesDue = open.filter((l) => (l.status === "prospect" || l.status === "contacted") && !l.do_not_contact)
    .filter((l) => { const n = leadNextAction(l, now); return n && (n.overdue || (n.due ?? "9") <= today); })
    .sort((a, b) => (a.next_touch_due ?? "").localeCompare(b.next_touch_due ?? ""));
  const notStarted = open.filter((l) => l.status === "prospect").length;
  const followUps = open.filter((l) => !["new", "call_booked", "prospect", "contacted"].includes(l.status)).filter((l) => {
    const n = leadNextAction(l, now);
    return n && (n.overdue || !n.due || n.due.slice(0, 10) <= in7);
  });

  // ---- Audits
  const reportsDue = audits.filter((a) => a.report_due && (a.status === "data_checked" || a.status === "analysed")).sort((a, b) => a.report_due!.localeCompare(b.report_due!));
  const lateReports = reportsDue.filter((a) => a.report_due! < today);
  const waiting = audits.filter((a) => a.status === "collecting");
  const implementing = audits.filter((a) => a.status === "implementing");
  const overdueActions = actions.filter((a) => a.due && a.due < today && byAudit.get(a.audit_id)?.status !== "closed");
  const purges = audits.filter((a) => a.purge_after && a.purge_after >= today && daysBetween(today, a.purge_after) <= 30);

  // ---- Promises and health
  const from = londonDate(new Date(now.getTime() - LOOKBACK_DAYS * 864e5));
  const reportsByAudit = new Map<string, ReportRow[]>(await Promise.all(
    audits.filter((a) => a.report_due && a.report_due >= from).map(async (a) => [a.id, await db.listReports(a.id).catch(() => [])] as [string, ReportRow[]]),
  ));
  const replyP = replyPromise(leads, now);
  const reportP = reportPromise(audits, reportsByAudit, now);
  const won = clientsThisMonth(leads, now);
  const value = pipelineValue(leads);
  const quiet = staleLeads(leads, now);
  const overrun = implementationsOverrun(audits, now);
  const lateTouches = touchesDue.filter((l) => leadNextAction(l, now)?.overdue).length;
  const lateFollowUps = followUps.filter((l) => leadNextAction(l, now)?.overdue).length;
  const needsNow = replies.filter((l) => { const n = leadNextAction(l, now); return n && (n.overdue || (n.due ?? "9").slice(0, 10) <= today); }).length
    + touchesDue.length + lateFollowUps + reportsDue.filter((a) => a.report_due! <= today).length + overdueActions.length + overrun.length;
  const lateCount = lateReplies.length + lateTouches + lateFollowUps + lateReports.length + overdueActions.length + overrun.length;
  const promiseTone = (r: number | null): "good" | "warn" | "bad" | undefined => (r === null ? undefined : r >= 0.9 ? "good" : r >= 0.75 ? "warn" : "bad");

  // ---- The next 7 days, as an agenda
  type Item = { day: string; when?: string; what: string; href: string; kind: "call" | "report" | "end" | "follow" };
  const agenda = ([
    ...calls.map((l) => ({ day: londonDate(new Date(l.call_at!)), when: new Date(l.call_at!).toLocaleTimeString("en-GB", { hour: "numeric", minute: "2-digit", timeZone: "Europe/London" }), what: `Discovery call: ${leadTitle(l)}`, href: `#/lead/${l.id}`, kind: "call" as const })),
    ...reportsDue.filter((a) => a.report_due! >= today && a.report_due! <= in7).map((a) => ({ day: a.report_due!, what: `Report due: ${a.client_name}`, href: `#/audit/${a.id}`, kind: "report" as const })),
    ...implementing.filter((a) => a.implementation_ends && a.implementation_ends >= today && a.implementation_ends <= in7).map((a) => ({ day: a.implementation_ends!, what: `Implementation ends: ${a.client_name}`, href: `#/audit/${a.id}`, kind: "end" as const })),
    ...followUps.filter((l) => { const n = leadNextAction(l, now); return n?.due && n.due.slice(0, 10) >= today && n.due.slice(0, 10) <= in7; }).map((l) => ({ day: leadNextAction(l, now)!.due!.slice(0, 10), what: `${leadNextAction(l, now)!.what}: ${leadTitle(l)}`, href: `#/lead/${l.id}`, kind: "follow" as const })),
  ] as Item[]).sort((a, b) => `${a.day}${a.when ?? ""}`.localeCompare(`${b.day}${b.when ?? ""}`));
  const dayLabel = (d: string) => d === today ? "Today" : daysBetween(today, d) === 1 ? "Tomorrow" : new Date(`${d}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "Europe/London" });
  const agendaCard = h("section", { class: "card agenda-card", "aria-labelledby": "h-week" },
    h("h2", { id: "h-week" }, icon("today"), "Next 7 days"),
    agenda.length
      ? h("ol", { class: "agenda" }, agenda.map((i) => h("li", { class: `ag-${i.kind}` },
          h("span", { class: "ag-day" }, dayLabel(i.day), i.when ? h("small", null, i.when) : ""),
          h("a", { href: i.href }, i.what))))
      : h("p", { class: "muted" }, "Nothing booked. A good week to research prospects."),
  );
  const quietCard = h("section", { class: "card", "aria-labelledby": "h-quiet" },
    h("h2", { id: "h-quiet" }, icon("history"), "Going quiet"),
    h("p", { class: "muted small" }, `Leads in conversation with no contact for ${STALE_DAYS} days or more. A short note keeps them warm.`),
    quiet.length
      ? h("ul", { class: "quiet-list" }, quiet.slice(0, 6).map((q) => h("li", null, h("a", { href: `#/lead/${q.lead.id}` }, leadTitle(q.lead)), h("span", { class: "quiet-days" }, `${q.quietDays} days`), stagePill(q.lead.status))))
      : notice("ok", "Every conversation has been touched in the last two weeks."),
  );

  const reportLeft = (a: AuditRow) => {
    const wd = workingDaysBetween(today, a.report_due!);
    return wd < 0 ? h("span", { class: "late" }, `${-wd} working day${wd === -1 ? "" : "s"} late`) : wd === 0 ? h("strong", null, "Due today") : `${wd} working day${wd === 1 ? "" : "s"} left`;
  };
  const leadRow = (l: LeadRow) => {
    const t = triage(l);
    return [h("a", { href: `#/lead/${l.id}` }, leadTitle(l)), l.orders_band ?? "–", t.offer, priorityPill(t.priority), nextCell(l, now)];
  };
  const hello = displayName(email);

  const hour = Number(now.toLocaleString("en-GB", { hour: "numeric", hour12: false, timeZone: "Europe/London" }));
  const greeting = t(hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening");
  const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;
  const summary: (string | HTMLElement)[] = [];
  if (lateReplies.length || lateReports.length || overdueActions.length) {
    summary.push(h("b", null, [lateReplies.length ? plural(lateReplies.length, "late reply", "late replies") : "", lateReports.length ? plural(lateReports.length, "late report") : "", overdueActions.length ? plural(overdueActions.length, "overdue action") : ""].filter(Boolean).join(", ")), " first. ");
  }
  summary.push(replies.length || touchesDue.length || calls.length
    ? `${plural(replies.length, "reply", "replies")} to send, ${plural(touchesDue.length, "outreach touch", "outreach touches")} due and ${plural(calls.length, "call")} in the next 7 days.`
    : "Nothing is waiting. A good day to research prospects.");

  const root = h("section", { class: "page" });
  mount(root,
    h("div", { class: "page-head" },
      h("h1", { ...langAttrs() }, hello ? `${greeting}, ${hello}` : greeting),
      h("span", { class: "due muted" }, now.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric", timeZone: "Europe/London" })),
    ),
    langNotice(),
    whatsNew(),
    h("p", { class: "today-sub" }, ...summary),
    h("div", { class: "quick", ...langAttrs() },
      h("a", { class: "btn", href: "#/leads?add" }, icon("plus"), t("Add a lead")),
      h("a", { class: "btn", href: "#/audits" }, icon("audits"), t("Audits")),
      h("a", { class: "btn", href: "#/tools" }, icon("tools"), t("Tools")),
      h("a", { class: "btn", href: "#/pipeline" }, icon("pipeline"), t("Pipeline")),
    ),
    h("div", { class: "stats stats-5" },
      stat("Needs you now", String(needsNow), lateCount ? `${lateCount} late` : needsNow ? "due today" : "all clear", lateCount ? "bad" : needsNow ? "warn" : "good"),
      stat("Replies on time", pctText(replyP.rate), replyP.due ? `${replyP.onTime} of ${replyP.due} in the last ${LOOKBACK_DAYS} days · within 2 working days` : "within 2 working days", promiseTone(replyP.rate)),
      stat("Reports on time", pctText(reportP.rate), reportP.due ? `${reportP.onTime} of ${reportP.due} · within 5 working days of data` : "within 5 working days of complete data", promiseTone(reportP.rate)),
      stat("Clients this month", String(won.won), "leads that became clients"),
      stat("Open pipeline", gbp(value.pence), `audit fees from ${value.counted} lead${value.counted === 1 ? "" : "s"}`),
    ),
    h("div", { class: "today-grid" },
    h("div", { class: "today-main" },
    h("div", { class: "card" },
      h("h2", null, "Replies due"),
      replies.length
        ? table(["Who", "Orders a month", "Offer", "Priority", "Reply by"], replies.map(leadRow))
        : notice("ok", "No one is waiting for a reply."),
    ),

    touchesDue.length
      ? h("div", { class: "card" },
          h("h2", null, "Outreach due"),
          table(["Who", "Orders a month", "Owner", "Next"], touchesDue.map((l) => [h("a", { href: `#/lead/${l.id}` }, leadTitle(l)), l.orders_band ?? "–", l.owner?.split("@")[0] ?? "–", nextCell(l, now)])),
        )
      : "",

    calls.length || followUps.length
      ? h("div", { class: "card" },
          h("h2", null, "Calls and follow-ups"),
          calls.length ? table(["Discovery call", "Who", "Orders a month", "Offer"], calls.map((l) => [h("strong", null, when(l.call_at, now)), h("a", { href: `#/lead/${l.id}` }, leadTitle(l)), l.orders_band ?? "–", triage(l).offer])) : "",
          followUps.length ? [h("h4", null, "Follow-ups"), table(["Who", "Stage", "Next"], followUps.map((l) => [h("a", { href: `#/lead/${l.id}` }, leadTitle(l)), stagePill(l.status), nextCell(l, now)]))] : "",
        )
      : "",

    h("div", { class: "card" },
      h("h2", null, "Audits"),
      reportsDue.length
        ? table(["Report due", "Client", "Audit", "Status", "Time left"], reportsDue.map((a) => [date(a.report_due), h("a", { href: `#/audit/${a.id}` }, a.client_name), a.title, statusPill(a.status), reportLeft(a)]))
        : notice("ok", "No reports due."),
      waiting.length
        ? [h("h4", null, "Waiting for data"), table(["Client", "Audit", "Created", "Waiting"], waiting.map((a) => [h("a", { href: `#/audit/${a.id}` }, a.client_name), a.title, date(a.created_at), `${daysBetween(londonDate(new Date(a.created_at)), today)} days`]))]
        : "",
      implementing.length
        ? [h("h4", null, "Implementation"), table(["Client", "Ends", "Open actions", "Overdue"], implementing.map((a) => {
            const mine = actions.filter((x) => x.audit_id === a.id);
            const late = mine.filter((x) => x.due && x.due < today).length;
            return [h("a", { href: `#/audit/${a.id}` }, a.client_name), date(a.implementation_ends), String(mine.length), late ? h("span", { class: "late" }, String(late)) : "0"];
          }), { numeric: [2, 3] })]
        : "",
    ),

    overrun.length
      ? h("div", { class: "card" }, h("h2", null, "Implementations past six weeks"),
          table(["Client", "Ended", "Audit"], overrun.map((a) => [h("a", { href: `#/audit/${a.id}` }, a.client_name), h("span", { class: "late" }, date(a.implementation_ends)), a.title])))
      : "",
    ),
    h("aside", { class: "today-side", "aria-label": "This week and leads going quiet" },
      agendaCard,
      quietCard,
      purges.length
        ? h("section", { class: "card" }, h("h2", null, "Client data to be deleted in the next 30 days"),
            h("p", { class: "muted small" }, "Cleaned order data is deleted 90 days after implementation ends. Reports and summaries are kept."),
            table(["Client", "Deleted on"], purges.map((a) => [h("a", { href: `#/audit/${a.id}` }, a.client_name), date(a.purge_after)])))
        : "",
    ),
    ),
  );
  return root;
}
